import { RuntimeMediaQuery } from '@duda-co/responsive-styles/RuntimeMediaQuery';
import type { ResponsiveStylesStructured } from '@duda-co/responsive-styles/types/ResponsiveStylesTypes';
import { ecomObserver } from '@shared-services/ecom-service';
import { localizationService } from '@shared-services/localization-service';
import { Div, Span } from 'client/widget-components/basicComponents';
import Text, {
    TextDomTag,
} from 'client/widget-components/basicComponents/Text';
import withErrorBoundary from 'client/widget-components/components/ErrorBoundary/withErrorBoundary';
import { ProductState } from 'client/widget-components/ecom/stores/ProductState';
import { ResponsiveStylesProp } from 'client/widget-components/responsive-styles/responsiveStylesService';
import React, { FC } from 'react';
import styled from 'styled-components';
import LowestPrice from './LowestPrice';
import PriceTaxLabel from './PriceTaxLabel';

export interface RuntimeProductPriceProps {
    productState: ProductState;
    _styles?: {
        price: ResponsiveStylesProp;
        compareAtPrice: ResponsiveStylesProp;
        frequency: ResponsiveStylesProp;
        frame: ResponsiveStylesProp;
    };
}

const RuntimeProductPrice: FC<RuntimeProductPriceProps> = ({
    _styles: styles,
    productState,
}) => {
    const productData = productState?.productData;
    if (!productData) {
        return null;
    }
    const frequency = productState.selectedPaymentPlan?.frequency;
    const compareAtPrice = productData.displayed_compare_at;
    return (
        <PriceWrapper
            data-auto='product-price-widget'
            data-grab='product-price-frame'
            styles={styles?.frame}
        >
            <Text
                tag={TextDomTag.h4}
                data-grab='product-compare-at-price'
                styles={[
                    compareAtPrice
                        ? compareAtPriceWrapperDefaultStyles
                        : compareAtPriceWrapperHiddenStyles,
                    styles?.compareAtPrice,
                ]}
            >
                <Span styles={compareAtPriceDefaultStyles}>
                    {productData.displayed_compare_at}
                </Span>
            </Text>
            <Text
                tag={TextDomTag.h4}
                data-grab='product-price'
                styles={[priceDefaultStyles, styles?.price]}
            >
                {productData.displayed_price}
            </Text>
            <Text
                tag={TextDomTag.h4}
                data-grab='product-frequency'
                styles={[frequencyDefaultStyles, styles?.frequency]}
            >
                {frequency && (
                    <>
                        /
                        {localizationService.str(
                            `ui.runtimessr.productPriceOptions.frequency.${frequency.toLowerCase()}`
                        )}
                    </>
                )}
            </Text>
            <PriceTaxLabel
                productState={productState}
                styles={[styles?.compareAtPrice, priceLabelStyles]}
            />
            {compareAtPrice && (
                <LowestPrice
                    productState={productState}
                    styles={[styles?.compareAtPrice, priceLabelStyles]}
                />
            )}
        </PriceWrapper>
    );
};

const PriceWrapper = styled(Div)`
    word-wrap: normal;
`;

const compareAtPriceWrapperHiddenStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        display: 'none',
    },
};

const compareAtPriceWrapperDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        display: 'inline',
        paddingRight: 8,
        margin: 0,
    },
};

const compareAtPriceDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        textDecoration: 'line-through',
    },
};

const priceDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        display: 'inline',
        margin: 0,
    },
};

const frequencyDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        display: 'inline',
        margin: 0,
    },
};

const priceLabelStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        fontSize: '13px',
        margin: 0,
    },
    [RuntimeMediaQuery.DESKTOP]: {
        fontSize: '13px',
        margin: 0,
    },
};

const RuntimeProductPriceWidget = withErrorBoundary({
    Comp: ecomObserver(RuntimeProductPrice),
    componentName: 'RuntimeProductPrice',
    additionalInfoToLog: {
        tag: 'native-ecom',
    },
});

export default RuntimeProductPriceWidget;
